import { motion } from "framer-motion";
import { SearchX, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import { MenuHeader, SubMenu } from "@/types/menu";

import { navVariants } from "../constants";
import { SidebarItem } from "./SidebarItem";

interface SidebarNavProps {
  menus: MenuHeader[];
  isLoading: boolean;
  hasError: boolean;
  isCollapsed: boolean;
  isSearching: boolean;
  expandedMenus: string[];
  currentPath: string;
  currentSearch: string;
  counts: Record<string, number>;
  onToggleMenu: (id: string) => void;
  onNavigate: (item: MenuHeader | SubMenu) => void;
}

export const SidebarNav = ({
  menus,
  isLoading,
  hasError,
  isCollapsed,
  isSearching,
  expandedMenus,
  currentPath,
  currentSearch,
  counts,
  onToggleMenu,
  onNavigate,
}: SidebarNavProps) => {
  const showEmptyState = !isLoading && !hasError && isSearching && menus.length === 0;

  return (
    <motion.nav
      className={cn(
        "sidebar-scrollbar flex-1 space-y-1.5 overflow-y-auto overflow-x-hidden py-3",
        // O flyout dos submenus é renderizado em portal (HoverCard), então a
        // nav pode rolar em ambos os modos sem recortar o painel.
        isCollapsed ? "px-1" : "px-3",
      )}
      variants={navVariants}
      initial="hidden"
      animate="show"
    >
      {!isLoading && hasError && (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center text-sidebar-foreground/50">
          <TriangleAlert className="h-6 w-6" />
          <span className="text-xs">Não foi possível carregar o menu</span>
        </div>
      )}

      {showEmptyState && (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center text-sidebar-foreground/50">
          <SearchX className="h-6 w-6" />
          <span className="text-xs">Nenhum menu encontrado</span>
        </div>
      )}

      {!isLoading &&
        !hasError &&
        menus.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            isCollapsed={isCollapsed}
            isExpanded={expandedMenus.includes(item.id)}
            currentPath={currentPath}
            currentSearch={currentSearch}
            counts={counts}
            onToggle={onToggleMenu}
            onNavigate={onNavigate}
          />
        ))}
    </motion.nav>
  );
};
